import { computed, ComputedRef, reactive, ref, shallowRef, watch } from 'vue';
import { ChartData, ChartOption, CustomColumn, CustomColumnsReadonly } from '@/common/utils/types';
import {
  columnFormatter,
  getTimeInfoApiParams,
  resolveAlertStatUnit,
  useAlertFormatter,
} from '@/alert/utils/utils';
import { getInitPeriodInfo } from '@/common/components/molecules/timeController';
import { TimePeriodInfo } from '@/common/components/molecules/timeController/types';
import {
  AlertDetailEmit,
  AlertDetailTabProps,
  AlertHistoryParams,
  AlertSubType,
  AlertType,
  RuleCriteria,
} from '@/alert/components/alertDetail/alertDetail.types';
import { getIntervalMs, transformResData, TransformResDataOpt } from '@/worker/utils';
import { TARGET_TYPE, useAlertTagsAndTargets } from '@/alert/utils/tagsAndTargets.uses';
import {
  getCustomPeriodInterval,
  getTimeAxisInfoByChartType,
  useEvChartStyle,
} from '@/common/utils/chartUtils';
import { CHECK_BY_COMBINATION, CHECK_BY_TARGET, NOTI_SENT_INFO } from '@/alert/utils/define';
import { useInternational } from '@/common/locale';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { useEventTypeValue } from '@/alert/components/alertGrid/alertGrid.uses';
import {
  isAlertDetailTreeMode,
  isCustomMetricSubType,
  prepareAlertDetailTreeSourceRows,
  resolveAlertDetailMetricSubType,
  type AlertMetricSubTypeInput,
} from '@/alert/utils/alertMetricSubType.utils';
import { ExportExcelOption } from '@/common/utils/exportExcelUtils';
import { useExportExcelFromServer } from '@/common/composables/useExportExcel';
import { USER_ENV_KEYS } from '@/common/define/userEnv.define';
import { useDashboardPopupEnvStore } from '@/common/stores/dashboard-popup';
import { NotificationStatusWindowInfo, NotificationType } from '@/alert/utils/notification.types';
import { NOTIFICATION_STATUS_WINDOW_INITIAL_INFO } from '@/alert/utils/notification.define';
import { normalizeNotificationMethodType } from '@/alert/utils/notification.utils';
import { storeToRefs } from 'pinia';
import { useAlertDetailStore } from '@/alert/stores/alert-detail';
import dayjs from 'dayjs';
import { formatTimeByInterval, standardTimeToUtcZeroTime } from '@/common/utils/commonUtils';
import {
  FIVE_MINUTES_IN_SECOND,
  ONE_DAY_IN_SECOND,
  type RequestInterval,
} from '@/common/utils/define';
import { isNil, lowerCase } from 'lodash-es';
import { useChartAxesFormatter } from '@/common/utils/chartUnit.utils';
import { isAxiosError } from 'axios';
import { isCancelError } from '@/common/utils/error.utils';
import { useMessageBox } from '@/common/inject/useMessageBox';
import { addIntervalToTime } from '@/common/utils/time.utils';
import { isClickedPause } from '@/common/components/molecules/timeController/utils';

import {
  AlertHistoryMetricsItem,
  AlertHistoryRequest,
  Query,
  NotificationMethod,
} from '@/api-client/alert/model';
import {
  postV9AlertsHistory,
  postV9AlertsHistoryMetrics,
  usePostV9AlertsHistory,
} from '@/api-client/alert/queries/alert-history-controller/alert-history-controller';
import { useMyInfoStore } from '@/config/stores/my-info';
import { useDashboardJumpStore } from '@/dashboard/stores/dashboard-jump';

import { blankAlertDetailTreeGridRows, getGridColumnType } from './alertDetail.utils';
import { ALERT_GRID_COLUMN_TYPE } from './alertDetail.define';
import {
  clampLinkedTimePeriodInfo,
  expandLinkedTimePeriodInfo,
} from '@/common/utils/linkedTimePeriod.utils';

export interface AlertHistoryTabProps extends AlertDetailTabProps {
  timePeriodInfo?: TimePeriodInfo;
}

export const CHART_COLOR = {
  CRITICAL: '#FF1F3F',
  WARNING: '#FFD759',
  NORMAL: '#12A480',
};
const NORMAL_BAR_HEIGHT_PX = 3;
const NORMAL_DIVIDE_VALUE = 60 / NORMAL_BAR_HEIGHT_PX;

const checkValueChartIndex = (chartData: { [key: string]: number[] }, dataIndex: number) => {
  return Object.values(chartData).some((val) => !!val[dataIndex]);
};

const findLastValidIndex = (_chartData) => {
  const { data, labels } = _chartData;
  const chartDataValues = Object.values(data || {});
  if (chartDataValues.length > 0 && Array.isArray(chartDataValues[0])) {
    let lastValidIndex = -1;

    for (let i = chartDataValues[0].length - 1; i >= 0; i--) {
      const hasValidValue = chartDataValues.some(
        (series) => Array.isArray(series) && series[i] !== undefined && series[i] !== null,
      );

      if (hasValidValue) {
        lastValidIndex = i;
        break;
      }
    }

    if (lastValidIndex >= 0) {
      return {
        index: lastValidIndex,
        time: labels[lastValidIndex],
      };
    }
  }
  return {
    index: undefined,
    time: '',
  };
};

const getMaxCriticalWarningSum = (
  seriesCount: { criticalCount?: number[]; warningCount?: number[] } | null,
): number => {
  if (
    !seriesCount ||
    !Array.isArray(seriesCount.criticalCount) ||
    !Array.isArray(seriesCount.warningCount)
  ) {
    return 0;
  }
  const { criticalCount, warningCount } = seriesCount;
  const length = Math.max(criticalCount.length, warningCount.length);
  let maxSum = 0;
  for (let i = 0; i < length; i += 1) {
    const sum = (criticalCount[i] ?? 0) + (warningCount[i] ?? 0);
    if (sum > maxSum) {
      maxSum = sum;
    }
  }
  return Math.ceil(maxSum * 1.1);
};

const useChart = () => {
  const { axisStyle, tooltipStyle, indicatorStyle } = useEvChartStyle();
  const chartIntervalRef = ref<string>('');
  const normalCountMap = ref<Map<string, number>>();

  const chartOption = reactive<ChartOption>({
    type: 'bar',
    width: '100%',
    height: '100',
    title: {
      show: false,
    },
    thickness: 0.6,
    legend: {
      show: false,
    },
    padding: {
      top: 4,
      right: 0,
      left: 0,
      bottom: 4,
    },
    indicator: {
      ...indicatorStyle,
      use: false,
    },
    selectLabel: {
      use: true,
      limit: 1,
      useDeselectOverflow: true,
      useApproximateValue: true,
      useSeriesOpacity: true,
      useLabelOpacity: false,
    },
    axesX: [
      {
        ...axisStyle,
        type: 'time',
        showAxis: true,
        showGrid: false,
        timeFormat: 'HH:mm:ss',
        categoryMode: true,
      },
    ],
    axesY: [
      {
        ...axisStyle,
        type: 'linear',
        showGrid: true,
        showAxis: false,
        startToZero: true,
        autoScaleRatio: 0.1,
        niceScale: true,
      },
    ],
    tooltip: {
      ...tooltipStyle,
      formatter: ({ x, y, seriesId }) => {
        if (seriesId === 'normal') {
          return `${normalCountMap.value?.get(x as string) ?? 0}`;
        }
        return `${y}`;
      },
      sortByValue: false,
    },
  });

  const chartData = ref<ChartData>({
    series: {
      normal: { name: 'Normal', color: CHART_COLOR.NORMAL },
      warning: { name: 'Warning', color: CHART_COLOR.WARNING },
      critical: { name: 'Critical', color: CHART_COLOR.CRITICAL },
    },
    groups: [['normal', 'warning', 'critical']],
    labels: [],
    data: {},
  });

  const initChartData = () => {
    chartData.value.labels = [];
    chartData.value.data = {
      critical: [],
      warning: [],
      normal: [],
    };
  };

  const { getXAxesFormatter } = useChartAxesFormatter();

  const setChartData = ({
    data,
    config,
    apiParams,
    query,
  }: {
    data: AlertHistoryMetricsItem[];
    config: any;
    apiParams: AlertHistoryParams;
    query: Query;
  }) => {
    if (!data.length && !query) {
      initChartData();
      return;
    }

    const interval = getCustomPeriodInterval(apiParams.period, {
      fromTime: +dayjs(apiParams.fromTime || query.fromTime),
      toTime: +dayjs(apiParams.toTime || query.toTime),
    });

    chartIntervalRef.value = interval;

    const { timeFormat, interval: chartInterval } = getTimeAxisInfoByChartType(interval, 'bar');

    const adjustedFromTime = formatTimeByInterval(query.fromTime!, interval as RequestInterval);
    const adjustedToTime = addIntervalToTime(query.toTime!, interval as RequestInterval);

    if (normalCountMap.value) {
      normalCountMap.value.clear();
    }

    const transformDataOpt: TransformResDataOpt = {
      type: 'chart',
      fromTime: interval === 'I1d' ? standardTimeToUtcZeroTime(adjustedFromTime) : adjustedFromTime,
      toTime: adjustedToTime,
      interval: interval.slice(1) || '1m',
      fields: ['triggerTime', 'criticalCount', 'warningCount', 'normalCount'],
      needDateTime: true,
      apiConfig: config,
    };

    const { data: seriesData, dateTime: labels } = transformResData(
      data ?? [],
      transformDataOpt,
    ) ?? {
      data: null,
      dateTime: [],
    };
    chartData.value.labels = labels.map((v) => +v) ?? [];
    normalCountMap.value = new Map(
      labels.map((label, index) => [label.valueOf(), seriesData?.normalCount?.[index] ?? 0]),
    );

    const maxSum = getMaxCriticalWarningSum(seriesData);
    const normalDivide = maxSum / NORMAL_DIVIDE_VALUE;

    chartData.value.data = {
      critical: seriesData?.criticalCount ?? [],
      warning: seriesData?.warningCount ?? [],
      normal: seriesData?.normalCount?.map((v) => (maxSum > 0 && v > 0 ? normalDivide : v)) ?? [],
    };

    chartOption.axesX![0].formatter = getXAxesFormatter({
      interval: typeof chartInterval === 'string' ? chartInterval : chartInterval.unit,
      timeFormat,
    });
    chartOption.axesX![0].interval = chartInterval;
  };

  // TODO: 대상 타겟이 있는 경우 파라미터 추가 필요
  const fetchData = async (
    _type: AlertType,
    apiParams: AlertHistoryParams,
    mutateAsync: (variables: { data: AlertHistoryRequest }) => Promise<any>,
  ) => {
    const { alertRuleId, alertTargetId, ...rest } = apiParams;
    try {
      const response = await mutateAsync({
        data: {
          ...rest,
          alertRuleIds: [alertRuleId!],
          targetId: alertTargetId,
        } as AlertHistoryRequest,
      });
      const {
        config,
        data: { data: alertHistoryData },
      } = response;

      const query = response.data.query || ({} as Query);

      setChartData({
        data: alertHistoryData?.filter((v) => v.totalCount && v.totalCount > 0) ?? [],
        config,
        apiParams,
        query,
      });
    } catch (error) {
      if (isAxiosError(error) && !isCancelError(error)) {
        throw error;
      }
    }
  };

  return {
    chartOption,
    chartData,
    fetchData,
    initChartData,
    chartIntervalRef,
    setChartData,
  };
};

const BASE_GRID_COLUMNS: CustomColumn[] = [
  {
    caption: 'Alert',
    field: 'alertLevel',
    type: 'string',
    rendererType: 'label',
    width: 100,
    searchable: true,
  },
  {
    caption: 'Alert Stat Name',
    field: 'alertName',
    type: 'string',
    width: 200,
    searchable: true,
    defaultSearchField: true,
  },
  {
    caption: 'Alert Value',
    field: 'value',
    type: 'string',
    sortable: false,
    width: 110,
    searchable: true,
  },
  {
    caption: 'Target',
    field: 'targets',
    type: 'string',
    rendererType: 'chip-cell',
    width: 250,
    searchable: true,
    defaultSearchField: true,
  },
  {
    caption: 'Alert Detail',
    field: 'subName',
    type: 'string',
    width: 150,
    searchable: true,
  },
  {
    caption: 'Triggered Time',
    field: 'triggerTime',
    type: 'string',
    width: 140,
    searchable: true,
  },
  {
    caption: 'Alias',
    field: 'targetAlias',
    type: 'string',
    rendererType: 'chip-cell',
    width: 150,
    searchable: true,
    defaultSearchField: true,
  },
  // {
  //   caption: 'Description',
  //   field: 'description',
  //   type: 'string',
  // },
  {
    caption: 'Notification Status',
    field: 'notificationStatus',
    type: 'string',
    sortable: false,
    width: 150,
    searchable: true,
  },
  {
    caption: 'Detail',
    field: 'detail',
    type: 'string',
    align: 'center',
    sortable: false,
    width: 120,
  },
  {
    caption: 'resultId',
    field: 'resultId',
    hide: true,
  },
  {
    caption: 'Event Type',
    field: 'eventType',
    hide: true,
  },
  {
    caption: 'Rule Type',
    field: 'alertRuleType',
    hide: true,
  },
  {
    caption: 'Threshold',
    field: 'threshold',
    type: 'string',
    hide: true,
  },
  {
    caption: 'Stat Unit',
    field: 'statUnit',
    type: 'string',
    hide: true,
  },
];

const ALERT_TYPE_TABLE_GRID_COLUMNS: CustomColumn[] = [
  {
    caption: 'Alert',
    field: 'alertLevel',
    type: 'string',
    rendererType: 'label',
    width: 100,
    searchable: true,
  },
  {
    caption: 'Alert Stat Name',
    field: 'alertName',
    type: 'string',
    width: 200,
    searchable: true,
    defaultSearchField: true,
  },
  {
    caption: 'Alert Value',
    field: 'value',
    type: 'string',
    sortable: false,
    width: 110,
    searchable: true,
  },
  {
    caption: 'Target',
    field: 'targets',
    type: 'string',
    rendererType: 'chip-cell',
    width: 250,
    searchable: true,
    defaultSearchField: true,
  },
  {
    caption: 'Alert Detail',
    field: 'subName',
    type: 'string',
    width: 150,
    searchable: true,
  },
  {
    caption: 'Triggered Time',
    field: 'triggerTime',
    type: 'string',
    width: 140,
    searchable: true,
  },
  {
    caption: 'Alias',
    field: 'targetAlias',
    type: 'string',
    rendererType: 'chip-cell',
    width: 150,
    searchable: true,
    defaultSearchField: true,
  },
  {
    caption: 'Threshold',
    field: 'threshold',
    type: 'string',
    sortable: false,
    width: 350,
  },
  {
    caption: 'Notification Status',
    field: 'notificationStatus',
    type: 'string',
    sortable: false,
    width: 150,
    searchable: true,
  },
  {
    caption: 'Detail',
    field: 'detail',
    type: 'string',
    align: 'center',
    sortable: false,
    width: 120,
  },
  {
    caption: 'resultId',
    field: 'resultId',
    hide: true,
  },
  {
    caption: 'Event Type',
    field: 'eventType',
    hide: true,
  },
  {
    caption: 'Rule Type',
    field: 'alertRuleType',
    hide: true,
  },
  {
    caption: 'Stat Unit',
    field: 'statUnit',
    type: 'string',
    hide: true,
  },
];

const EXTERNAL_BASE_GRID_COLUMNS: CustomColumn[] = [
  {
    caption: 'Label',
    field: 'subName',
    type: 'string',
    searchable: true,
  },
  {
    caption: 'Value',
    field: 'value',
    type: 'string',
    width: 100,
    searchable: true,
  },
  {
    caption: 'Alert',
    field: 'alertLevel',
    type: 'string',
    rendererType: 'label',
    width: 100,
    searchable: true,
  },
  {
    caption: 'Triggered Time',
    field: 'triggerTime',
    width: 140,
    type: 'string',
    searchable: true,
  },
  {
    caption: 'Notification Status',
    field: 'notificationStatus',
    type: 'string',
    sortable: false,
    width: 150,
    searchable: true,
  },
  {
    caption: 'Detail',
    field: 'detail',
    type: 'string',
    align: 'center',
    sortable: false,
    width: 120,
  },
  {
    caption: 'resultId',
    field: 'resultId',
    hide: true,
  },
  {
    caption: 'Event Type',
    field: 'eventType',
    hide: true,
  },
];

const useTab = ({
  ruleCriteria,
  subType,
  periodInfo,
}: {
  ruleCriteria: ComputedRef<RuleCriteria>;
  subType: ComputedRef<AlertSubType | undefined>;
  periodInfo: ComputedRef<TimePeriodInfo>;
}) => {
  const { t } = useInternational();
  const { timezone } = storeToRefs(useMyInfoStore());
  const { confirmMsg } = useMessageBox();
  const { userAlertInformationData } = storeToRefs(useAlertDetailStore());

  const tabPanels = [
    {
      text: t('WORD.SORT_BY_TIME'),
      value: 'sortByTime',
    },
  ];
  const selectedTab = ref('sortByTime');

  const historySourceRows = ref<AlertMetricSubTypeInput[]>([]);

  const metricConditionSubType = computed(() =>
    resolveAlertDetailMetricSubType({
      isMetricRule: subType.value === 'metric',
      ruleCriteria: ruleCriteria.value,
      ruleDetail: userAlertInformationData.value,
      firstAlertRow: historySourceRows.value[0],
    }),
  );

  const hasChildren = ref(false);
  const isTreeMode = computed(() =>
    isAlertDetailTreeMode({
      metricSubType: metricConditionSubType.value,
      ruleCriteria: ruleCriteria.value,
      hasChildren: hasChildren.value,
      alertRows: historySourceRows.value,
    }),
  );

  const gridColumnType = computed(() => {
    return getGridColumnType({
      subType: subType.value ?? 'metric',
      ruleCriteria: ruleCriteria.value,
      metricConditionSubType: metricConditionSubType.value,
    });
  });

  const gridColumns = computed<CustomColumn[]>(() => {
    if (gridColumnType.value === ALERT_GRID_COLUMN_TYPE.EXTERNAL) {
      return EXTERNAL_BASE_GRID_COLUMNS;
    }

    if (gridColumnType.value === ALERT_GRID_COLUMN_TYPE.TABLE) {
      return ALERT_TYPE_TABLE_GRID_COLUMNS;
    }

    if (gridColumnType.value === ALERT_GRID_COLUMN_TYPE.METRIC_COMBINATION) {
      return [
        {
          caption: 'Rule Name',
          field: 'alertRuleName',
          type: 'string',
          width: 200,
          searchable: true,
        },
        ...BASE_GRID_COLUMNS,
      ];
    }

    if (gridColumnType.value === ALERT_GRID_COLUMN_TYPE['SYSTEM|EVENT_STATISTICS']) {
      return [...BASE_GRID_COLUMNS.filter((v) => v.field !== 'alertName')];
    }

    return BASE_GRID_COLUMNS;
  });
  const gridData = shallowRef<any[][]>([]);

  const { makeAlertRows, makeAlertTreeRows } = useAlertTagsAndTargets();
  const alertFormatter = useAlertFormatter();

  const makeRowsController = {
    flat: makeAlertRows,
    tree: makeAlertTreeRows,
  };

  const setGridData = (rows) => {
    if (!rows) {
      return;
    }
    if (rows && !rows.length) {
      gridData.value = [];
      historySourceRows.value = [];
      hasChildren.value = false;
      return;
    }

    historySourceRows.value = rows;
    hasChildren.value = rows.some((row) => row.child?.length);
    const resolvedMetricSubType = metricConditionSubType.value;
    const useTreeMode = isAlertDetailTreeMode({
      metricSubType: resolvedMetricSubType,
      ruleCriteria: ruleCriteria.value,
      hasChildren: hasChildren.value,
      alertRows: rows,
    });
    const shouldFilterFormulaChild = isCustomMetricSubType(resolvedMetricSubType) || useTreeMode;
    const sourceRows = shouldFilterFormulaChild
      ? prepareAlertDetailTreeSourceRows(rows, resolvedMetricSubType)
      : rows;
    const makeRows = makeRowsController[useTreeMode ? 'tree' : 'flat'];
    const rawRows = makeRows({
      data: sourceRows,
      columns: gridColumns.value,
      targetType: TARGET_TYPE.TARGETS,
      formatter: {
        alertName: (_, { alertName, alertRuleName, alertRuleType, parent, threshold, child }) => {
          if (useTreeMode && child?.length) return null;
          // metric/table 행은 statId를 표시명으로 변환 (룰 목록과 동일)
          const ruleType = lowerCase(alertRuleType || parent?.alertRuleType);
          if (ruleType === 'metric' || ruleType === 'table') {
            return (
              threshold?.formulaAlias || alertFormatter.alertName(alertName || threshold?.alertName)
            );
          }
          return threshold?.formulaAlias || alertName || alertRuleName;
        },
        alertLevel: (_, { alertLevel }) => columnFormatter.alert(alertLevel),
        notiSent: (v) => NOTI_SENT_INFO[v] ?? '-',
        targetAlias: (_, { targets }) => {
          if (useTreeMode || !Array.isArray(targets)) return null;

          const result = targets.reduce<string[]>((acc, target) => {
            if (target.alias) {
              acc.push(target.alias);
            }
            return acc;
          }, []);

          return result.length ? result : null;
        },
        statUnit: (_, row) => resolveAlertStatUnit(row, alertFormatter),
        notificationStatus: (v: NotificationMethod[]) =>
          (v ?? []).map((status) => ({
            ...status,
            methodType: normalizeNotificationMethodType(status.methodType),
          })),
      },
      hasNullRow: true,
    });

    // 트리 모드(복합/사용자 정의): 첫 행 subType이 CUSTOM/COMPOUND일 때만 root 행은 Alert Stat Name·Alert Value 비움, 자식 행은 Alert Stat Name·Alert Value만 표시 (Figma 스펙)
    gridData.value = useTreeMode
      ? blankAlertDetailTreeGridRows(rawRows, resolvedMetricSubType)
      : rawRows;
  };

  // AlertHistoryRequest
  const fetchTabData = {
    sortByTime: async (
      _type: AlertType,
      apiParams: AlertHistoryParams,
      mutateAsync: (variables: { data: AlertHistoryRequest; params?: any }) => Promise<any>,
    ) => {
      const { alertRuleId, alertTargetId, ...rest } = apiParams;
      try {
        const response = await mutateAsync({
          data: {
            ...rest,
            alertRuleIds: [alertRuleId!],
            targetId: alertTargetId,
          } as AlertHistoryRequest,
        });
        const { data } = response.data;

        setGridData(data);
      } catch (error) {
        console.log(error);
      }
    },
  };

  const lastApiParams = ref<{ type: AlertType; apiParams: AlertHistoryParams }>({
    type: 'user',
    apiParams: {} as AlertHistoryParams,
  });
  const fetchData = async (
    type: AlertType,
    apiParams: AlertHistoryParams,
    mutateAsync: (variables: { data: AlertHistoryRequest; params?: any }) => Promise<any>,
  ) => {
    lastApiParams.value = { type, apiParams };
    await fetchTabData[selectedTab.value]?.(type, apiParams, mutateAsync);
  };

  const OOM_ERROR_CODE = 'CE006';
  const OOM_HTTP_STATUS = 413;

  const exportExcelFromServer = useExportExcelFromServer();
  const exportToExcelFromServer = async (
    exportExcelOption: ExportExcelOption & {
      columns?: CustomColumn[] | CustomColumnsReadonly;
    },
  ) => {
    const { apiParams } = lastApiParams.value;
    const { alertRuleId, fromTime: _fromTime, toTime: _toTime, ...rest } = apiParams;
    const { fromTimeUtc: fromTime, toTimeUtc: toTime } = periodInfo.value;
    const reportColumns =
      exportExcelOption.columns
        ?.filter(({ hide, field }) => !hide && !!field)
        .map(({ field, attrs, caption }) => {
          // TODO: 콤비네이션 Alert일때 Alias 컬럼 미사용이 스펙이라면 그냥 column 제외하는 방식으로 변경하는게 좋을듯(위의 makeRows 함수에서 처리하는 방식도 필요 없어 보임)
          const fieldName =
            field === 'targetAlias' && ruleCriteria.value === CHECK_BY_COMBINATION ? '' : field;
          return {
            fieldName,
            displayName: attrs?.excelCaption || caption,
          };
        }) ?? [];

    const postV9AlertsHistoryAdapter = (params: AlertHistoryRequest) =>
      postV9AlertsHistory(
        params,
        {
          isReport: true,
          timezone: timezone.value,
        },
        {
          params: {
            frameName: FRAME_NAMES.ALERT_DETAIL.HISTORY_SORT_BY_TIME,
          },
          responseType: 'blob',
        },
      );

    // OOM 에러 감지 어댑터: OOM 발생 시 Confirm 창 출력 후 기본 에러 메시지 억제
    const oomAwareAdapter = async (params: AlertHistoryRequest) => {
      try {
        return await postV9AlertsHistoryAdapter(params);
      } catch (e) {
        if (!isAxiosError(e) || !e.response) throw e;
        // responseType:'blob' 요청이라 에러 응답 본문도 Blob으로 도착한다.
        // 텍스트로 풀어 JSON 파싱한 뒤 errorCode(type)를 확인한다.
        const { data } = e.response;
        let errorType: string | undefined;
        if (data instanceof Blob) {
          try {
            const parsed: unknown = JSON.parse(await data.text());
            if (
              parsed &&
              typeof parsed === 'object' &&
              'type' in parsed &&
              typeof parsed.type === 'string'
            ) {
              errorType = parsed.type;
            }
          } catch {
            // JSON 파싱 실패 → OOM 아님으로 처리
          }
        }
        if (errorType === OOM_ERROR_CODE || e.response.status === OOM_HTTP_STATUS) {
          confirmMsg({
            msgStr: `<div class="excel-oom-msg">${t('MESSAGE.EXCEL_DOWNLOAD_OOM')}</div>`,
            showCancelBtn: true,
            useHTML: true,
          });
          // useExportExcelFromServer의 'canceled' 조기 종료 분기를 활용해 showErrorMsg 억제
          throw new Error('canceled');
        }
        throw e;
      }
    };

    await exportExcelFromServer(
      oomAwareAdapter,
      {
        ...rest,
        alertRuleIds: [alertRuleId!],
        fromTime,
        reportColumns,
        toTime,
      } as AlertHistoryRequest,
      exportExcelOption,
    );
  };

  return {
    tabPanels,
    selectedTab,
    gridColumnType,
    gridColumns,
    gridData,
    isTreeMode,
    fetchData,
    setGridData,
    exportToExcelFromServer,
  };
};

export const setup = (props: AlertHistoryTabProps, emit: AlertDetailEmit) => {
  const { t } = useInternational();

  const { fetchDashboardJumpList } = useDashboardJumpStore();
  const { selectedRuleInfo: ruleInfo } = storeToRefs(useAlertDetailStore());
  const { confirmMsg } = useMessageBox();

  const periodInfo = ref<TimePeriodInfo>(
    props.timePeriodInfo
      ? clampLinkedTimePeriodInfo(
          expandLinkedTimePeriodInfo(props.timePeriodInfo, FIVE_MINUTES_IN_SECOND),
          ONE_DAY_IN_SECOND,
        )
      : getInitPeriodInfo(),
  );

  const lastTimeValue = ref<{
    index?: number;
    time: string;
  }>({
    index: undefined,
    time: '',
  });
  const chartClickFlag = ref(false);
  const selectedLabel = ref<{ dataIndex: number[] }>({ dataIndex: [] });
  const chartRef = ref();
  const lastClickLabel = ref<string>('');
  const barClickFromTime = ref<string>('');
  const barClickToTime = ref<string>('');
  const isClickPauseButton = ref<boolean>(false);

  // AbortController for chart metrics
  const chartMetricsAbortController = ref<AbortController | null>(null);

  // Chart metrics adapter (signal을 받아서 base function에 전달)
  const postV9AlertsHistoryMetricsAdapter = (
    args: AlertHistoryRequest & {
      frameName?: string;
      signal?: AbortSignal;
    },
  ) => {
    const { frameName, signal, ...params } = args;
    return postV9AlertsHistoryMetrics(
      params,
      {
        params: {
          frameName: frameName || FRAME_NAMES.ALERT_DETAIL.HISTORY_TREND,
        },
      },
      signal,
    );
  };

  // Tab history mutation
  const { mutateAsync: fetchHistoryMutate } = usePostV9AlertsHistory({
    request: {
      params: {
        frameName: FRAME_NAMES.ALERT_DETAIL.HISTORY_SORT_BY_TIME,
      },
    },
  });

  const {
    chartOption,
    chartData,
    fetchData: _fetchChartData,
    initChartData,
    chartIntervalRef,
    setChartData,
  } = useChart();

  const {
    tabPanels,
    selectedTab,
    gridColumnType,
    gridColumns,
    gridData,
    isTreeMode,
    fetchData: fetchTabData,
    setGridData,
    exportToExcelFromServer,
  } = useTab({
    ruleCriteria: computed(() => ruleInfo.value.ruleCriteria || CHECK_BY_TARGET),
    subType: computed(() => ruleInfo.value.subType),
    periodInfo: computed(() => periodInfo.value),
  });

  const isGridLoading = ref(false);
  // 탭 최초 진입 또는 룰 변경 시점에만 로딩 마스크를 표시하기 위한 플래그
  const isFirstTabLoad = ref(false);

  const callTabData = async (
    info: {
      fromTimeUtc: string;
      toTimeUtc: string;
      isPaused?: boolean;
    },
    showLoading = false,
  ) => {
    // 차트 바 클릭(사용자 명시 인터랙션) 시에만 로딩 마스크 표시
    if (showLoading) {
      isGridLoading.value = true;
    }

    await fetchTabData(
      ruleInfo.value.alertRuleType,
      {
        alertRuleId: ruleInfo.value.alertRuleId,
        ...getTimeInfoApiParams({ ...periodInfo.value, ...info }, false),
        alertTargetId: ruleInfo.value.alertTargetId,
        signal: undefined as any,
      },
      fetchHistoryMutate,
    )
      .catch((err) => {
        if (isAxiosError(err) && !isCancelError(err)) {
          emit('api-error', {
            message: err.response?.data?.message || err.message,
            isError: true,
          });
        }
      })
      .finally(() => {
        isFirstTabLoad.value = false;
        isGridLoading.value = false;
      });
  };

  const setChartLabelDataIndex = (dataIndex?: number) => {
    selectedLabel.value.dataIndex = !isNil(dataIndex) ? [dataIndex] : [];
  };

  const notificationStatusWindowInfo = ref<NotificationStatusWindowInfo>({
    ...NOTIFICATION_STATUS_WINDOW_INITIAL_INFO,
  });

  /** 알림 발송 상태 팝업: 라이브 타임바(예: 5초) 갱신과 분리된 조회용 시간대 */
  const notificationStatusWindowTimeSnapshot = ref<TimePeriodInfo | null>(null);

  const notificationStatusWindowTimePeriodInfo = computed(() => {
    return notificationStatusWindowTimeSnapshot.value ?? periodInfo.value;
  });

  const onClickStatusButton = (payload: { iconType: NotificationType; row: any }) => {
    const { iconType, row } = payload;
    const { resultId } = row;
    const notificationStatus = row.notificationStatus as NotificationMethod[];

    const availableStatus = notificationStatus.map(
      (status) => status.methodType,
    ) as NotificationType[];

    notificationStatusWindowTimeSnapshot.value = { ...periodInfo.value };
    notificationStatusWindowInfo.value = {
      isShow: true,
      availableStatus,
      resultId,
      selectedType: iconType,
    };
  };

  const onUpdatedIndicator = async (info: TimePeriodInfo) => {
    isClickPauseButton.value = isClickedPause(periodInfo.value, info);
    periodInfo.value = info;
    if (isClickPauseButton.value) return;

    if (!props.isShow) {
      return;
    }

    // 이전 요청 취소
    if (chartMetricsAbortController.value) {
      chartMetricsAbortController.value.abort();
    }

    // 새로운 AbortController 생성
    chartMetricsAbortController.value = new AbortController();

    const apiParams = {
      alertRuleId: ruleInfo.value.alertRuleId,
      ...getTimeInfoApiParams(info, true),
      alertTargetId: ruleInfo.value.alertTargetId,
    };

    try {
      const response = await postV9AlertsHistoryMetricsAdapter({
        ...apiParams,
        alertRuleIds: [apiParams.alertRuleId!],
        targetId: apiParams.alertTargetId,
        frameName: FRAME_NAMES.ALERT_DETAIL.HISTORY_TREND,
        signal: chartMetricsAbortController.value.signal,
      } as AlertHistoryRequest & { frameName?: string; signal?: AbortSignal });

      const {
        config,
        data: { data: alertHistoryData, query },
      } = response;

      setChartData({
        data: alertHistoryData?.filter((v) => v.totalCount && v.totalCount > 0) ?? [],
        config,
        apiParams: apiParams as AlertHistoryParams,
        query: query || ({} as Query),
      });
    } catch (err) {
      if (isAxiosError(err) && !isCancelError(err)) {
        emit('api-error', {
          message: err.response?.data?.message || err.message,
          isError: true,
        });
      }
    }

    lastTimeValue.value = findLastValidIndex(chartData.value);

    if (chartClickFlag.value && !periodInfo.value.isPaused) {
      const findIndex = chartData.value.labels.findIndex((v) => v === lastClickLabel.value);
      if (findIndex >= 0) {
        setChartLabelDataIndex(findIndex);
      } else {
        chartClickFlag.value = false;
        setChartLabelDataIndex(lastTimeValue.value.index);
      }
    } else if (!periodInfo.value.isPaused && !chartClickFlag.value) {
      setChartLabelDataIndex(lastTimeValue.value.index);
    }
  };

  const { isShowMessageWindow, eventAlertInfo, onClickValueCell } = useEventTypeValue();
  const onClickCell = async ({ field, value, row }) => {
    if (field === 'value') {
      await onClickValueCell({
        value,
        row,
        ruleId: ruleInfo.value.alertRuleId,
        targetFrameName: FRAME_NAMES.ALERT_DETAIL.HISTORY_SORT_BY_TIME,
      });
    }
  };

  let prevIsPaused = false;
  watch(isShowMessageWindow, (val) => {
    if (val) {
      prevIsPaused = periodInfo.value.isPaused;
      periodInfo.value.isPaused = true;
    } else {
      periodInfo.value.isPaused = prevIsPaused;
    }
  });

  const onChartClick = (target) => {
    const { label, dataIndex } = target;
    if (label && !isNil(dataIndex) && checkValueChartIndex(chartData.value.data!, dataIndex)) {
      const clickUtcTime = standardTimeToUtcZeroTime(+label);
      chartClickFlag.value = true;
      lastClickLabel.value = label;

      chartMetricsAbortController.value?.abort();

      const clickUtcToTime = dayjs(clickUtcTime)
        .add(getIntervalMs(chartIntervalRef.value).msTime, 'ms')
        .format('YYYY-MM-DD HH:mm:ss');
      setChartLabelDataIndex(dataIndex);
      const { fromTimeUtc: periodFromTimeUtc, toTimeUtc: periodToTimeUtc } = periodInfo.value;
      const finalFromTimeUtc = dayjs(clickUtcTime).isBefore(dayjs(periodFromTimeUtc))
        ? periodFromTimeUtc
        : clickUtcTime;

      const finalToTimeUtc = dayjs(clickUtcToTime).isAfter(dayjs(periodToTimeUtc))
        ? periodToTimeUtc
        : clickUtcToTime;

      barClickFromTime.value = finalFromTimeUtc;
      barClickToTime.value = finalToTimeUtc;

      callTabData(
        {
          fromTimeUtc: finalFromTimeUtc,
          toTimeUtc: finalToTimeUtc,
          isPaused: true,
        },
        true,
      );
    }
  };

  const onclickExcelExport = (option: ExportExcelOption) => {
    confirmMsg({
      msgStr: t('MESSAGE.ALERT_HISTORY_EXPORT_CONFIRM'),
      okCallback: () => exportToExcelFromServer(option),
      showCancelBtn: true,
    });
  };

  watch(
    [() => props.isShow, ruleInfo],
    async ([isShow], [prevIsShow, prevRuleInfo]) => {
      if (isShow) {
        // 룰 변경 판정은 history/metrics API 식별자(alertRuleId + alertTargetId) 기준.
        // 폴링 churn(식별자 동일·새 참조)은 false 유지 → 갱신마다 로딩 깜빡이는 현상 방지.
        const ruleChanged =
          !!prevRuleInfo &&
          (prevRuleInfo.alertRuleId !== ruleInfo.value.alertRuleId ||
            prevRuleInfo.alertTargetId !== ruleInfo.value.alertTargetId);

        if (!prevIsShow || ruleChanged) {
          isFirstTabLoad.value = true;
        }

        if (ruleChanged) {
          setGridData([]);
          initChartData();
        }
        try {
          // ruleInfo가 변경되었거나 isPaused 상태일 때만 직접 fetch
          // isShow가 false→true로 변경되고 isPaused가 false인 경우:
          //   LiveUpdater가 onMounted 또는 onVisibilityChange를 통해 자동으로 fetch하므로 생략
          if (ruleChanged || periodInfo.value.isPaused) {
            chartRef.value?.redraw();
            emit('update:isLoading', true);
            await fetchDashboardJumpList();

            await onUpdatedIndicator(periodInfo.value);
          }
        } finally {
          emit('update:isLoading', false);
        }

        if (!prevIsShow || ruleChanged) {
          const { fetchDashboardPopupEnvInfoForKey } = useDashboardPopupEnvStore();
          const envKey = USER_ENV_KEYS.ALERT_LIST_DETAIL_HISTORY_LIST_DASHBOARD_SETTING_LIST;
          await fetchDashboardPopupEnvInfoForKey(envKey);
        }
      } else {
        chartMetricsAbortController.value?.abort();
        initChartData();
        setGridData([]);
        chartClickFlag.value = false;
      }
    },
    { immediate: true },
  );

  watch(
    [() => periodInfo.value.isPaused, () => lastTimeValue.value],
    async ([isPaused, _lastTimeValue]) => {
      if (isPaused && isClickPauseButton.value) return;

      if (_lastTimeValue?.time && !chartClickFlag.value) {
        const utcTime = standardTimeToUtcZeroTime(_lastTimeValue.time);
        barClickFromTime.value = utcTime;
        barClickToTime.value = dayjs(utcTime)
          .add(getIntervalMs(chartIntervalRef.value).msTime, 'ms')
          .format('YYYY-MM-DD HH:mm:ss');
        if (isPaused) {
          setChartLabelDataIndex(_lastTimeValue.index);
        }
        await callTabData({
          fromTimeUtc: barClickFromTime.value,
          toTimeUtc: barClickToTime.value,
          isPaused: true,
        });
      } else if (isPaused || !chartClickFlag.value) {
        setChartLabelDataIndex(undefined);
        setGridData([]);
        // 조회 결과가 없어 그리드 fetch(callTabData)를 타지 않는 경우에도
        // 전체 탭 로딩 마스크는 해제한다 (마스크가 갇히는 것 방지).
        isFirstTabLoad.value = false;
      }
    },
  );

  const isSelectedChartLabel = computed(() => {
    return selectedLabel.value.dataIndex.length > 0;
  });

  watch(
    () => periodInfo.value.isPaused,
    (isPaused) => {
      if (!isPaused) {
        chartClickFlag.value = false;
      }
    },
  );

  watch(
    () => notificationStatusWindowInfo.value.isShow,
    (show) => {
      if (!show) {
        notificationStatusWindowTimeSnapshot.value = null;
      }
    },
  );

  return {
    periodInfo,
    onUpdatedIndicator,

    ruleInfo,

    chartOption,
    chartData,

    tabPanels,
    selectedTab,
    gridColumnType,
    gridColumns,
    gridData,
    isTreeMode,

    isShowMessageWindow,
    eventAlertInfo,
    onClickCell,
    exportToExcelFromServer,
    t,
    onChartClick,
    selectedLabel,
    chartRef,
    barClickFromTime,
    barClickToTime,
    onclickExcelExport,
    notificationStatusWindowInfo,
    notificationStatusWindowTimePeriodInfo,
    onClickStatusButton,

    isSelectedChartLabel,
    isGridLoading,
    isFirstTabLoad,
  };
};
